import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Activity, Check, ChevronRight, Sparkles } from 'lucide-react';
import { FigIcon } from '../components/FigIcon';

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSelectPackage = (packageId) => {
    if (user) {
      navigate(`/checkout?package=${packageId}`);
    } else {
      navigate(`/register?redirect=checkout&package=${packageId}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FigIcon className="w-6 h-6 text-[#2F3E30]" />
            <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
          </Link>
          {user ? (
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-[#525252] hover:text-[#1A1C19]"
            >
              Dashboard
            </Button>
          ) : (
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="text-[#525252] hover:text-[#1A1C19]"
            >
              Log in
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              CHOOSE YOUR PATH
            </h1>
            <p className="text-[#525252] max-w-2xl mx-auto">
              Start with a free assessment, then unlock the insights you need
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Results Only */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#10B981]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'Libre Baskerville, serif' }}>RESULTS ONLY</h3>
              <p className="text-[#525252] text-sm mb-4">See your score and risk factors</p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>£5</span>
                <span className="text-[#525252] text-sm">one-time</span>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                {[
                  'Metabolic Trajectory Score',
                  'Risk tier assessment',
                  'Top 5 risk drivers',
                  'Component breakdown'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#10B981]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-[#EBEBE6] border border-[#D1D1CD] hover:bg-[#D1D1CD] text-[#1A1C19]"
                onClick={() => handleSelectPackage('results')}
                data-testid="pricing-results-btn"
              >
                Get Results
              </Button>
            </div>

            {/* Full Program - One-time */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 relative overflow-hidden border-[#2F3E30]/50">
              <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[#2F3E30]/20 text-[#2F3E30] text-xs font-medium">
                POPULAR
              </div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#2F3E30]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'Libre Baskerville, serif' }}>FULL PROGRAM</h3>
              <p className="text-[#525252] text-sm mb-4">Results + 90-day correction plan</p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>£49</span>
                <span className="text-[#525252] text-sm">one-time</span>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                {[
                  'Everything in Results',
                  'Personalized 90-day plan',
                  '4 targeted correction rules',
                  'Weekly check-in tracking',
                  'Progress dashboard',
                  'Email reminders'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#10B981]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-[#2F3E30] hover:bg-[#3A4D3B] text-white glow-primary"
                onClick={() => handleSelectPackage('one_time')}
                data-testid="pricing-one-time-btn"
              >
                Get Full Program
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Subscription */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 relative overflow-hidden">
              <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-medium">
                FLEXIBLE
              </div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#F59E0B]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'Libre Baskerville, serif' }}>3-MONTH PLAN</h3>
              <p className="text-[#525252] text-sm mb-4">Monthly payments, full access</p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>£19</span>
                <span className="text-[#525252] text-sm">/mo × 3</span>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                {[
                  'Everything in Full Program',
                  'Lower upfront cost',
                  'Cancel anytime',
                  'Priority support',
                  'Plan adjustments included'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#10B981]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-[#EBEBE6] border border-[#D1D1CD] hover:bg-[#D1D1CD] text-[#1A1C19]"
                onClick={() => handleSelectPackage('subscription')}
                data-testid="pricing-subscription-btn"
              >
                Start Monthly Plan
              </Button>
            </div>
          </div>

          {/* Free Assessment Note */}
          <div className="max-w-2xl mx-auto text-center">
            <div className="p-6 rounded-xl bg-[#EBEBE6] border border-[#D1D1CD]">
              <Sparkles className="w-8 h-8 text-[#F59E0B] mx-auto mb-3" />
              <h3 className="font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>FREE ASSESSMENT</h3>
              <p className="text-sm text-[#525252] mb-4">
                The metabolic assessment questionnaire is completely free. 
                Take it now and only pay when you're ready to see your results.
              </p>
              {!user?.questionnaire_completed && (
                <Button 
                  onClick={() => navigate(user ? '/questionnaire' : '/register')}
                  variant="outline"
                  className="border-[#D1D1CD]"
                >
                  Start Free Assessment
                </Button>
              )}
            </div>
          </div>

          {/* FAQ Link */}
          <div className="text-center mt-8">
            <p className="text-[#525252] mb-2">Have questions?</p>
            <Link to="/#faq" className="text-[#2F3E30] hover:underline">
              View our FAQ
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
